'use strict';

const libPictProvider = require('pict-provider');

// Drives the Headlight Academy full-page view. Owns no doc content of its own — it reuses the inline-doc
// provider (`Pict-InlineDocumentation`): `AppData.InlineDocumentation.SidebarGroups` for the TOC and
// `_fetchDocument(DocsBaseURL + path)` for a rendered (AcademyContent-layered) article, so the Academy, the
// help panel, and the doc editor always agree. Navigation between articles is client-side (no view re-render);
// the route only mounts the shell.

class AcademyProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._current = null;      // path of the article shown (e.g. 'organizations.md')
		this._targetPath = null;   // path the pending mount should open
		this._filter = '';         // lowercased TOC filter text
		this._clickBound = false;
	}

	get docProvider() { return this.pict.providers['Pict-InlineDocumentation'] || null; }
	get docState() { return (this.pict.AppData && this.pict.AppData.InlineDocumentation) || {}; }
	/** The host application's documentationProfile. Read from the application itself rather than only
	 *  from AppData, because this screen can render BEFORE the help panel publishes it there — and a
	 *  half-initialized read would paint the host app's name over a micro app's Academy. */
	get docProfile()
	{
		const tmpPublished = this.pict.AppData && this.pict.AppData.DocumentationProfile;
		if (tmpPublished) { return tmpPublished; }
		const tmpApplication = this.pict.PictApplication;
		return (tmpApplication && tmpApplication.documentationProfile) || {};
	}
	get docsBase() { return this.docState.DocsBaseURL || this.docProfile.DocsBaseURL || 'docs/'; }
	get landingArticle() { return this.docProfile.LandingArticle || 'overview.md'; }
	get groups() { return this.docState.SidebarGroups || []; }

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	/** Normalize a route topic ('organizations', 'organizations.md', '#/page/foo.md') to a doc path ('foo.md'). */
	_resolvePath(pTopic)
	{
		if (!pTopic) { return this.landingArticle; }
		let tmpPath = String(pTopic).replace(/^#?\/?(page\/)?/, '').replace(/^\.\//, '').replace(/^\//, '').replace(/#.*$/, '');
		if (!tmpPath) { return this.landingArticle; }
		if (!/\.md$/i.test(tmpPath)) { tmpPath += '.md'; }
		return tmpPath;
	}

	/** Set the article a pending route should open. The router calls this BEFORE (re-)rendering the shell, so the
	 *  view's onAfterRender → onViewRendered opens the right doc (not the overview default). */
	prepare(pTopic)
	{
		this._targetPath = this._resolvePath(pTopic);
	}

	/** When the shell is already mounted (route change without a re-render), swap the target article in place. */
	showTargetIfMounted()
	{
		if (this._targetPath && this._targetPath !== this._current) { this.openArticle(this._targetPath, false); }
	}

	/** Called by the view once its shell DOM exists. Ensure the TOC data is loaded, paint it, wire clicks, open. */
	onViewRendered()
	{
		// The shell ships neutral copy; the app names itself here so a micro app does not present its
		// inspectors with "Guides and reference for configuring your platform."
		this._paintHeading();
		this._ensureSidebar(() =>
		{
			this._paintHeading();
			this._bindClicks();
			this.renderTOC();
			this.openArticle(this._targetPath || this._current || this.landingArticle, false);
		});
	}

	/**
	 * The Academy can be the first screen to ask for documentation, before the help panel has ever been
	 * opened. Until the app initializes the inline-doc provider it has no docs root, so a sidebar load
	 * fetches nothing and the table of contents paints empty and never recovers. Kick the initialization
	 * first, then load.
	 */
	/** The shell ships the host app's copy; the running app names itself here, so a micro app does not
	 *  greet its inspectors with "Guides and reference for configuring your platform." */
	_paintHeading()
	{
		const tmpProfile = this.docProfile;
		if (!tmpProfile.Title) { return; }
		this.pict.ContentAssignment.assignContent('#Academy-Title', this._esc(tmpProfile.Title));
		this.pict.ContentAssignment.assignContent('#Academy-Subtitle', this._esc(tmpProfile.Subtitle || ''));
	}

	_ensureSidebar(fCallback)
	{
		if (this.groups.length) { return fCallback(); }
		const tmpApplication = this.pict.PictApplication;
		if (tmpApplication && !tmpApplication._helpInitialized && (typeof tmpApplication._initializeHelpDocumentation === 'function'))
		{
			tmpApplication._initializeHelpDocumentation();
		}
		const tmpDoc = this.docProvider;
		if (tmpDoc && typeof tmpDoc._loadSidebar === 'function') { return tmpDoc._loadSidebar(() => fCallback()); }
		return fCallback();
	}

	_matchesFilter(pItem)
	{
		if (!this._filter) { return true; }
		return String(pItem.Name || '').toLowerCase().indexOf(this._filter) !== -1;
	}

	renderTOC()
	{
		const tmpHTML = this.groups.map((pGroup) =>
		{
			const tmpItems = (pGroup.Items || []).filter((pItem) => this._matchesFilter(pItem)).map((pItem) =>
			{
				const tmpActive = (pItem.Path === this._current) ? ' ha-active' : '';
				const tmpIndent = pItem.Level ? ` style="padding-left:${10 + (pItem.Level * 14)}px"` : '';
				return `<a class="ha-toc-item${tmpActive}" data-ha-path="${this._esc(pItem.Path)}"${tmpIndent}>${this._esc(pItem.Name)}</a>`;
			}).join('');
			if (!tmpItems) { return ''; }
			return `<div class="ha-toc-group"><div class="ha-toc-group-title">${this._esc(pGroup.Name)}</div>${tmpItems}</div>`;
		}).join('');
		this.pict.ContentAssignment.assignContent('#Academy-TOC', tmpHTML || '<div class="ha-toc-empty">No articles match your filter.</div>');
	}

	onFilterInput(pText)
	{
		this._filter = String(pText || '').toLowerCase().trim();
		this.renderTOC();
	}

	/** Fetch + render an article into the article pane (reusing the inline-doc pipeline), update TOC highlight and
	 *  the hash route. pUpdateRoute=false when the load was itself driven by a route change. */
	openArticle(pPath, pUpdateRoute)
	{
		const tmpPath = this._resolvePath(pPath);
		this._current = tmpPath;
		this.renderTOC();
		this.pict.ContentAssignment.assignContent('#Academy-Article', '<div class="ha-loading"><span class="ha-spinner"></span> Loading…</div>');

		const tmpDoc = this.docProvider;
		if (!tmpDoc || typeof tmpDoc._fetchDocument !== 'function')
		{
			this.pict.ContentAssignment.assignContent('#Academy-Article', '<div class="ha-empty"><i class="fas fa-triangle-exclamation"></i><p>Documentation is unavailable.</p></div>');
			return;
		}
		tmpDoc._fetchDocument(this.docsBase + tmpPath, (pError, pHTML) =>
		{
			if (this._current !== tmpPath) { return; }   // a newer article was requested; drop this paint
			if (pError || !pHTML)
			{
				this.pict.ContentAssignment.assignContent('#Academy-Article', `<div class="ha-empty"><i class="fas fa-triangle-exclamation"></i><p>Could not load &ldquo;${this._esc(tmpPath)}&rdquo;.</p></div>`);
				return;
			}
			this.pict.ContentAssignment.assignContent('#Academy-Article', `<article class="ha-doc">${pHTML}</article>`);
			const tmpEl = this.pict.ContentAssignment.getElement('#Academy-Article');
			if (tmpEl && tmpEl[0]) { tmpEl[0].scrollTop = 0; }
		});

		if (pUpdateRoute !== false && typeof window !== 'undefined' && window.location)
		{
			const tmpWant = `/Academy/${tmpPath.replace(/\.md$/i, '')}`;
			if (window.location.hash.replace(/^#/, '') !== tmpWant) { window.location.hash = tmpWant; }
		}
	}

	/** One delegated click handler (survives shell re-renders) for TOC items (data-ha-path) and in-doc cross-links
	 *  (the inline-doc link resolver marks them rel="pict-inline-doc-link:<path>"). */
	_bindClicks()
	{
		if (this._clickBound || typeof document === 'undefined') { return; }
		this._clickBound = true;
		document.addEventListener('click', (pEvent) =>
		{
			const tmpTarget = pEvent.target && pEvent.target.closest ? pEvent.target.closest('[data-ha-path], a[rel^="pict-inline-doc-link:"]') : null;
			if (!tmpTarget || !tmpTarget.closest('#Academy-Container')) { return; }
			pEvent.preventDefault();
			let tmpPath = tmpTarget.getAttribute('data-ha-path');
			if (!tmpPath)
			{
				const tmpRel = tmpTarget.getAttribute('rel') || '';
				tmpPath = tmpRel.replace(/^pict-inline-doc-link:/, '');
			}
			if (tmpPath) { this.openArticle(tmpPath, true); }
		});
	}
}

AcademyProvider.default_configuration =
{
	ProviderIdentifier: 'Academy',
	AutoInitialize: true,
	AutoInitializeOrdinal: 260,
};

module.exports = AcademyProvider;
